/**
 * EnrichmentPage — Data completeness meters and gap actions.
 * Shows field coverage %, gap list, and CSV bulk upload.
 */

import { useState, useEffect, useRef } from 'react';
import { Upload, CheckCircle, AlertCircle, RefreshCw } from 'lucide-react';
import Header from '../components/layout/Header';
import Panel from '../components/common/Panel';
import api from '../services/api';

interface EnrichmentStats {
  total_contacts: number;
  email_pct: number;
  phone_pct: number;
  title_pct: number;
  company_pct: number;
  linkedin_pct: number;
  overall_pct: number;
}

interface EnrichmentSuggestion {
  type: string;
  count: number;
  pct_affected: number;
  priority: 'high' | 'medium' | 'low';
  action: string;
  filter_url: string;
}

interface OpportunitiesResponse {
  stats: Record<string, number>;
  completeness_pct: number;
  suggestions: EnrichmentSuggestion[];
}

const PRIORITY_COLORS: Record<string, string> = {
  high:   '#ef4444',
  medium: '#f59e0b',
  low:    '#3b82f6',
};

const FIELD_LABELS: Record<string, { label: string; color: string }> = {
  email:    { label: 'Email',    color: '#3b82f6' },
  phone:    { label: 'Phone',    color: '#22c55e' },
  title:    { label: 'Title',    color: '#f59e0b' },
  company:  { label: 'Company',  color: '#a855f7' },
  linkedin: { label: 'LinkedIn', color: '#0ea5e9' },
};

function CompletenessBar({ label, pct, color }: { label: string; pct: number; color: string }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>{label}</span>
        <span className="text-xs font-mono" style={{ color }}>
          {pct.toFixed(1)}%
        </span>
      </div>
      <div
        className="h-2 rounded-full overflow-hidden"
        style={{ background: 'rgba(255,255,255,0.06)' }}
      >
        <div
          className="h-2 rounded-full transition-all duration-700"
          style={{ width: `${pct}%`, background: color }}
        />
      </div>
    </div>
  );
}

export default function EnrichmentPage() {
  const [stats, setStats] = useState<EnrichmentStats | null>(null);
  const [opportunities, setOpportunities] = useState<OpportunitiesResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState<{ updated: number; errors: number } | null>(null);
  const [uploadError, setUploadError] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  const loadData = async () => {
    setLoading(true);
    try {
      const [statsRes, oppsRes] = await Promise.allSettled([
        api.get('/enrichment/stats'),
        api.get('/enrichment/opportunities'),
      ]);
      if (statsRes.status === 'fulfilled') setStats(statsRes.value.data);
      if (oppsRes.status === 'fulfilled') setOpportunities(oppsRes.value.data);
    } catch {
      // degrade silently
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    setCsvFile(file);
    setUploadResult(null);
    setUploadError('');
  };

  const handleCsvUpload = async () => {
    if (!csvFile) return;
    setUploading(true);
    setUploadError('');
    setUploadResult(null);

    try {
      const text = await csvFile.text();
      const lines = text.trim().split('\n');
      if (lines.length < 2) {
        setUploadError('CSV must have at least a header row and one data row.');
        return;
      }

      const headers = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/"/g, ''));
      const updates = lines.slice(1).map(line => {
        const vals = line.split(',').map(v => v.trim().replace(/"/g, ''));
        const record: Record<string, string | number> = {};
        headers.forEach((h, i) => {
          if (vals[i] !== undefined && vals[i] !== '') {
            record[h] = h === 'id' ? parseInt(vals[i], 10) : vals[i];
          }
        });
        return record;
      }).filter(r => r.id);

      if (updates.length === 0) {
        setUploadError('No valid rows found. Make sure CSV has an "id" column.');
        return;
      }

      const res = await api.post('/enrichment/bulk-update', { updates });
      setUploadResult(res.data);
      await loadData();
    } catch (err: any) {
      setUploadError(err?.response?.data?.detail || 'Upload failed — check CSV format.');
    } finally {
      setUploading(false);
      setCsvFile(null);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const overallPct = stats?.overall_pct ?? opportunities?.completeness_pct ?? 0;
  const overallColor = overallPct >= 80 ? '#22c55e' : overallPct >= 60 ? '#f59e0b' : '#ef4444';

  return (
    <div className="page-enter">
      <Header
        title="Data Enrichment"
        subtitle="Fill data gaps to improve warmth scoring and outreach"
      />

      <div className="p-4 md:p-8 space-y-6">

        {/* Overall completeness hero */}
        <div
          className="rounded-xl p-6 flex items-center gap-6"
          style={{
            background: `${overallColor}08`,
            border: `1px solid ${overallColor}20`,
          }}
        >
          <div className="text-center" style={{ minWidth: 80 }}>
            <p className="text-xs uppercase tracking-wider mb-1" style={{ color: 'var(--text-muted)' }}>
              Overall
            </p>
            <p className="font-display text-4xl" style={{ color: overallColor }}>
              {overallPct.toFixed(0)}%
            </p>
            <p className="text-[10px] mt-0.5" style={{ color: 'var(--text-muted)' }}>complete</p>
          </div>
          <div className="flex-1">
            <div className="h-3 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.06)' }}>
              <div
                className="h-3 rounded-full transition-all duration-1000"
                style={{ width: `${overallPct}%`, background: overallColor }}
              />
            </div>
            <p className="text-xs mt-2" style={{ color: 'var(--text-muted)' }}>
              {stats?.total_contacts?.toLocaleString() ?? '—'} total contacts
              {overallPct < 80 && ' — enrichment recommended'}
            </p>
          </div>
        </div>

        {/* Two-column: field meters + suggestions */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

          {/* Field completeness meters */}
          <Panel title="Field Coverage">
            {loading ? (
              <div className="py-6 text-sm text-center" style={{ color: 'var(--text-muted)' }}>Loading...</div>
            ) : stats ? (
              <div className="space-y-4">
                {Object.entries(FIELD_LABELS).map(([key, { label, color }]) => (
                  <CompletenessBar
                    key={key}
                    label={label}
                    pct={stats[`${key}_pct` as keyof EnrichmentStats] as number ?? 0}
                    color={color}
                  />
                ))}
              </div>
            ) : (
              <p className="text-sm py-4" style={{ color: 'var(--text-muted)' }}>No data available.</p>
            )}
          </Panel>

          {/* Gap suggestions */}
          <Panel title="Enrichment Gaps" noPadding>
            {loading && (
              <div className="px-5 py-6 text-sm text-center" style={{ color: 'var(--text-muted)' }}>Loading...</div>
            )}
            {!loading && (opportunities?.suggestions?.length ?? 0) === 0 && (
              <div className="px-5 py-8 text-center">
                <CheckCircle size={28} className="mx-auto mb-2" style={{ color: '#22c55e' }} />
                <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
                  No significant gaps detected.
                </p>
              </div>
            )}
            {!loading && (opportunities?.suggestions?.length ?? 0) > 0 && (
              <div className="divide-y" style={{ borderColor: 'var(--border-primary)' }}>
                {opportunities!.suggestions.map(s => (
                  <div key={s.type} className="px-5 py-3.5 flex items-start gap-3">
                    <AlertCircle
                      size={14}
                      className="flex-shrink-0 mt-0.5"
                      style={{ color: PRIORITY_COLORS[s.priority] }}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                          {s.count.toLocaleString()} contacts
                        </span>
                        <span
                          className="text-[10px] px-1.5 py-0.5 rounded"
                          style={{
                            background: `${PRIORITY_COLORS[s.priority]}15`,
                            color: PRIORITY_COLORS[s.priority],
                          }}
                        >
                          {s.priority}
                        </span>
                        <span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>
                          ({s.pct_affected}% affected)
                        </span>
                      </div>
                      <p className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>
                        {s.action}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Panel>
        </div>

        {/* CSV Bulk Upload */}
        <Panel title="Bulk Enrichment via CSV">
          <div className="space-y-4">
            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
              Upload a CSV with an <code className="px-1 py-0.5 rounded text-xs"
                style={{ background: 'rgba(255,255,255,0.08)', color: 'var(--netrun-green)' }}>id</code> column
              and any fields to update: <code className="px-1 py-0.5 rounded text-xs"
                style={{ background: 'rgba(255,255,255,0.08)', color: 'var(--text-muted)' }}>
                email, phone, title, company, linkedin_url, first_name, last_name, location, website
              </code>.
            </p>

            {/* Drop zone */}
            <div
              className="rounded-xl border-2 border-dashed p-6 flex flex-col items-center gap-3 transition-all cursor-pointer"
              style={{
                borderColor: csvFile ? 'var(--netrun-green)' : 'var(--border-primary)',
                background: csvFile ? 'rgba(144,185,171,0.05)' : 'transparent',
              }}
              onClick={() => fileRef.current?.click()}
            >
              <Upload
                size={28}
                style={{ color: csvFile ? 'var(--netrun-green)' : 'var(--text-muted)' }}
              />
              <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                {csvFile ? csvFile.name : 'Click to select a CSV file'}
              </p>
              {csvFile && (
                <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                  {(csvFile.size / 1024).toFixed(1)} KB
                </p>
              )}
              <input
                ref={fileRef}
                type="file"
                accept=".csv,text/csv"
                className="hidden"
                onChange={handleFileSelect}
              />
            </div>

            {csvFile && (
              <button
                onClick={handleCsvUpload}
                disabled={uploading}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm"
                style={{
                  background: 'var(--netrun-green)',
                  color: '#000',
                  border: 'none',
                  cursor: uploading ? 'not-allowed' : 'pointer',
                  opacity: uploading ? 0.7 : 1,
                  fontWeight: 500,
                }}
              >
                <RefreshCw size={14} className={uploading ? 'animate-spin' : ''} />
                {uploading ? 'Uploading...' : 'Upload & Enrich'}
              </button>
            )}

            {uploadResult && (
              <div
                className="flex items-center gap-2 text-sm px-4 py-3 rounded-lg"
                style={{ background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.2)' }}
              >
                <CheckCircle size={16} style={{ color: '#22c55e' }} />
                <span style={{ color: '#22c55e' }}>
                  Updated {uploadResult.updated.toLocaleString()} contacts
                  {uploadResult.errors > 0 && ` · ${uploadResult.errors} errors`}
                </span>
              </div>
            )}

            {uploadError && (
              <div
                className="flex items-center gap-2 text-sm px-4 py-3 rounded-lg"
                style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)' }}
              >
                <AlertCircle size={16} style={{ color: '#ef4444' }} />
                <span style={{ color: '#ef4444' }}>{uploadError}</span>
              </div>
            )}
          </div>
        </Panel>
      </div>
    </div>
  );
}
